import type { LucideIcon } from 'lucide-react';
import {
  BellIcon,
  CheckIcon,
  KeyboardIcon,
  ListTodoIcon,
  EyeIcon,
  EyeOffIcon,
  GitCommitHorizontalIcon,
  LinkIcon,
  PlayIcon,
  RotateCcwIcon,
  SquareIcon,
  LayoutGridIcon,
  PlugIcon,
  PlusIcon,
  SquareKanbanIcon,
  SunMoonIcon,
  UserRoundIcon,
  WorkflowIcon,
} from 'lucide-react';

import {
  ADMIN_ENTRY,
  ADMIN_NAV,
  ALL_DESTINATIONS,
  visibleNavItems,
  type NavViewer,
} from '@/features/app-shell';
import { AI_HELP_COPY, EDITOR_SETTINGS_PATH } from '@/features/editor-access';
import { opensOnBoard, workflowDestination } from '@/features/workflows';
import type { TaskView, WorkflowView } from '@/lib/api/generated/model';

/**
 * The kinds of thing the palette can offer.
 *
 * The kind is what a reader sees as well as what the code branches on: a task called
 * "Usage" and the Usage page must not look alike, which is the whole of spec 077 FR-010.
 */
export type EntryKind = 'workspace' | 'page' | 'object' | 'action';

/**
 * Which section an entry appears under, and — through `GROUP_ORDER` — where.
 *
 * Actions and workspaces sit above objects because a workspace with hundreds of tasks
 * must not push the one page you wanted below two hundred rows (FR-013).
 */
export type EntryGroup =
  'this-task' | 'recent' | 'actions' | 'workspaces' | 'in-workspace' | 'pages';

export const GROUP_ORDER: EntryGroup[] = [
  // First: with a task open, what to do with it is the likeliest question.
  'this-task',
  'recent',
  'actions',
  'workspaces',
  'in-workspace',
  'pages',
];

export const GROUP_LABEL: Record<EntryGroup, string> = {
  'this-task': 'This task',
  recent: 'Recent',
  actions: 'Actions',
  workspaces: 'Workspaces',
  'in-workspace': 'In this workspace',
  pages: 'Pages',
};

/** What a reader is told this entry *is*, beside its name. */
export const KIND_LABEL: Record<EntryKind, string> = {
  workspace: 'Workspace',
  page: 'Page',
  object: 'In this workspace',
  action: 'Action',
};

export interface PaletteEntry {
  /**
   * Stable and unique across kinds. A workspace and a task may share a name; they may
   * not share an id, which is what lets recents resolve one without matching the other.
   */
  id: string;
  kind: EntryKind;
  group: EntryGroup;
  /** What the reader searches and reads. */
  label: string;
  /** Said beside the label where the kind alone is ambiguous — a workflow and its board. */
  detail?: string;
  icon: LucideIcon;
  /** Where it goes. Absent on an action, which does something in place. */
  to?: string;
  params?: Record<string, string>;
  /** Search parameters the destination needs — the task a board should open. */
  search?: Record<string, unknown>;
  /** What an action runs. Absent on a destination. */
  act?: ActionId;
  /**
   * What the action is aimed at, when one action has several targets: the
   * step a task starts at, or the step a decision moves it to.
   */
  actArg?: string;
  /**
   * Other words a reader might type for it (spec 129 FR-010): "keys" finds
   * Secrets, "integrations" finds Connections.
   */
  keywords?: readonly string[];
}

/** Synonyms per page label. */
const PAGE_KEYWORDS: Record<string, readonly string[]> = {
  Secrets: [
    'keys',
    'env',
    'environment',
    'tokens',
    'credentials',
    'variables',
    'password',
  ],
  Connections: [
    'integrations',
    'github',
    'gitlab',
    'jira',
    'registry',
    'repository',
  ],
  Triggers: ['automation', 'webhook', 'rules', 'schedule'],
  // The grouped entries (spec 224) answer to the names of the pages they hold.
  Integrations: ['connections', 'triggers', 'secrets', 'github', 'gitlab'],
  'AI usage': ['usage', 'models', 'spend', 'cost', 'llm'],
  Library: ['agents', 'skills', 'templates', 'install', 'marketplace'],
  Files: ['documents', 'folders', 'upload', 'storage'],
  Tasks: ['list', 'all tasks', 'table'],
  Workflows: ['boards', 'builder', 'pipelines'],
  Spend: ['usage', 'cost', 'billing', 'tokens'],
  Models: [
    'ai models',
    'model',
    'llm',
    'profile',
    'claude',
    'reasoning',
    'tokens',
  ],
  Settings: [
    'labels',
    'preferences',
    'execution images',
    'docker',
    'container',
    'image',
  ],
  // Settings' tabs (spec 227 FR-009), under the words each had as a section.
  General: ['name', 'visibility', 'private', 'organization', 'remove'],
  People: ['members', 'owners', 'requests', 'join', 'access'],
  Activity: ['log', 'audit', 'history', 'who'],
  Labels: ['tags'],
  'Execution images': ['images', 'docker', 'container', 'registry', 'image'],
};

/** The actions the palette offers. The only authored list in this feature. */
export type ActionId =
  | 'new-task'
  | 'new-workspace'
  | 'toggle-theme'
  // The open task's actions (spec 125 FR-014), run by its drawer.
  | 'task-start'
  | 'task-decide'
  | 'task-stop'
  | 'task-run-again'
  | 'task-hide'
  | 'task-copy-link'
  | 'task-start-children'
  | 'add-secret'
  | 'add-connection'
  | 'add-execution-image'
  | 'new-trigger'
  | 'add-workflow'
  | 'upload-file'
  | 'shortcuts';

export interface EntrySources {
  viewer: NavViewer;
  /**
   * Whether MCP is on here, so the AI tools page is worth going to from
   * anywhere (spec 248). Absent: not known yet, and not offered.
   */
  mcpOn?: boolean;
  /** `marks`: Private, Owner, View only — what it is to the reader (spec 196 US3). */
  workspaces: { id: string; name: string; marks?: readonly string[] }[];
  /** The workspace whose contents are searchable, if any. */
  current?: {
    id: string;
    workflows: WorkflowView[];
    tasks: TaskView[];
    /**
     * The board the reader is looking at, when they are on one. The only place a
     * task can be created is a board's own New task control, so this is what
     * decides whether the palette offers that action (FR-015: the palette runs
     * what the equivalent control runs, and offers nothing that has no control).
     */
    boardWorkflowKey?: string;
    /**
     * The task whose drawer is open, and what it can do now (spec 125). The
     * palette offers these and the drawer runs them; the palette never acts on
     * a task it cannot see.
     */
    openTask?: OpenTaskActions;
  };
}

/** One way to take the open task's next step: a step to start at, or a way on. */
export interface NextTaskAction {
  act: 'task-start' | 'task-decide';
  /** The step it starts the task at, or moves it to. */
  stepId: string;
  label: string;
}

export interface OpenTaskActions {
  id: string;
  /**
   * Every way to take the next step, in the order `nextTaskActions` lists
   * them: where it starts first, then the other steps it may start at; or
   * each way on from a gate. Empty when the next step is Stop, Run again, or
   * nothing — those have entries of their own.
   */
  next: NextTaskAction[];
  isRunning: boolean;
  /**
   * What Stop is called for this task: "Stop waiting" when the run in flight
   * waits on the tasks it created (spec 140 US5). Absent means "Stop".
   */
  stopLabel?: string;
  /** Created tasks the open join waits on someone to start (spec 140 US2). */
  waitingToStart?: number;
  /** Whether Run again would repeat work (`canRunAgain`, spec 249). */
  canRunAgain: boolean;
  /** Stands on a human gate, where Run again is not offered (spec 172). */
  atGate?: boolean;
  isHidden: boolean;
}

/**
 * The open task's actions (spec 125 FR-014): every way to take its next step
 * first — each start step, or each way on from a gate, as its own entry, since
 * the drawer no longer has a button to choose between them (feedback
 * 2026-10-06) — then the card menu's verbs that apply now.
 */
/**
 * Whether the reader may change things here: false only for someone reading an
 * Organization workspace they are not in (spec 188), who is offered no verb that
 * would only be refused.
 */
function mayWork(sources: EntrySources): boolean {
  return sources.viewer.isWorkspaceMember ?? true;
}

export function taskEntries(sources: EntrySources): PaletteEntry[] {
  const open = sources.current?.openTask;
  if (!open) return [];
  const entries: PaletteEntry[] = [];
  const add = (act: ActionId, label: string, icon: LucideIcon) => {
    entries.push({
      id: `task:${act}`,
      kind: 'action',
      group: 'this-task',
      label,
      icon,
      act,
    });
  };
  // Someone reading the workspace gets the link and nothing that changes the task.
  if (!mayWork(sources)) {
    add('task-copy-link', 'Copy link to this task', LinkIcon);
    return entries;
  }
  for (const next of open.next) {
    entries.push({
      id: `task:${next.act}:${next.stepId}`,
      kind: 'action',
      group: 'this-task',
      label: next.label,
      icon: next.act === 'task-start' ? PlayIcon : CheckIcon,
      act: next.act,
      actArg: next.stepId,
    });
  }
  // Not exclusive with Stop below (spec 140): a parent waiting on its children
  // is running (Stop reads "Stop waiting") while some of them still wait to
  // be started, and both are offered.
  if (open.waitingToStart && open.waitingToStart > 0) {
    add(
      'task-start-children',
      `Start ${String(open.waitingToStart)} waiting tasks`,
      PlayIcon,
    );
  }
  if (open.isRunning) {
    add('task-stop', open.stopLabel ?? 'Stop', SquareIcon);
  }
  if (!open.isRunning && open.canRunAgain && !open.atGate) {
    add('task-run-again', 'Run again', RotateCcwIcon);
  }
  if (open.isHidden) add('task-hide', 'Show on the board', EyeIcon);
  else if (!open.isRunning) add('task-hide', 'Hide from the board', EyeOffIcon);
  add('task-copy-link', 'Copy link to this task', LinkIcon);
  return entries;
}

/**
 * Every destination the application has, for this viewer.
 *
 * **Read from `app-shell`'s registry. Never re-authored here** — this is the decision
 * that shapes the feature (spec 077 research R1). That module's own comment explains
 * why: `ALL_NAV_ITEMS` is walked by tests as the list of places a user can go, so a
 * palette offering a different set would leave that constant asserting something untrue,
 * and the two would drift on the first route added with nothing failing.
 *
 * Gating is `visibleNavItems`, unchanged, with the palette as its third caller after the
 * two shells. A gated page is **absent**, not disabled — and, as the registry says, that
 * is courtesy rather than security: the service refuses regardless.
 */
export function pageEntries(sources: EntrySources): PaletteEntry[] {
  const workspaceId = sources.current?.id;

  const workspacePages: PaletteEntry[] = workspaceId
    ? visibleNavItems(ALL_DESTINATIONS, sources.viewer).map((item) => ({
        id: `page:${item.to}`,
        kind: 'page' as const,
        group: 'pages' as const,
        label: item.label,
        icon: item.icon,
        to: item.to,
        params: { workspaceId },
        keywords: PAGE_KEYWORDS[item.label],
      }))
    : [];

  // The console's pages take no `workspaceId`, which is why they are a separate union in
  // the registry and a separate branch here.
  const adminPages: PaletteEntry[] = visibleNavItems(
    [ADMIN_ENTRY, ...ADMIN_NAV],
    sources.viewer,
  ).map((item) => ({
    id: `page:${item.to}:${item.label}`,
    kind: 'page' as const,
    group: 'pages' as const,
    label:
      item.label === 'Organization admin' ? item.label : `Admin: ${item.label}`,
    icon: item.icon,
    to: item.to,
  }));

  return [...workspacePages, ...adminPages];
}

/**
 * The person's own pages, reachable from anywhere: every notification (spec
 * 147 US4), and their commits and linked accounts (spec 166). Outside the registry, which lists a workspace's and the
 * console's destinations; this one belongs to neither.
 */
export function personalEntries(mcpOn = false): PaletteEntry[] {
  return [
    {
      id: 'page:/notifications',
      kind: 'page',
      group: 'pages',
      label: 'Go to notifications',
      icon: BellIcon,
      to: '/notifications',
      keywords: ['inbox', 'bell', 'alerts', 'archived'],
    },
    // Who you are here, and signing out (spec 239).
    {
      id: 'page:/settings/account',
      kind: 'page',
      group: 'pages',
      label: 'Go to your account',
      icon: UserRoundIcon,
      to: '/settings/account',
      keywords: ['settings', 'profile', 'sign out', 'switch account', 'admin'],
    },
    // How the agent's commits name you, and your linked GitLab and GitHub
    // accounts (spec 166): found by what people call it, not only its title.
    {
      id: 'page:/settings/commits',
      kind: 'page',
      group: 'pages',
      label: 'Go to commits and linked accounts',
      icon: GitCommitHorizontalIcon,
      to: '/settings/commits',
      keywords: [
        'linked accounts',
        'gitlab',
        'github',
        'token',
        'author',
        'commit email',
        'sign in',
      ],
    },
    // Found by what people call it (spec 248): the AI tools page was reachable
    // only through the settings rail. Only where MCP is on.
    ...(mcpOn
      ? [
          {
            id: `page:${EDITOR_SETTINGS_PATH}`,
            kind: 'page' as const,
            group: 'pages' as const,
            label: AI_HELP_COPY.paletteEntry,
            icon: PlugIcon,
            to: EDITOR_SETTINGS_PATH,
            keywords: [
              'mcp',
              'claude',
              'claude code',
              'copilot',
              'codex',
              'connect',
              'editor',
              'ai',
            ],
          },
        ]
      : []),
  ];
}

/** Every workspace the viewer can see. Searchable from anywhere, including outside one. */
export function workspaceEntries(sources: EntrySources): PaletteEntry[] {
  return sources.workspaces.map((workspace) => ({
    id: `workspace:${workspace.id}`,
    kind: 'workspace',
    group: 'workspaces',
    label: workspace.name,
    // Beside the name, as a workflow's kind is: the marks the home card shows.
    ...(workspace.marks && workspace.marks.length > 0
      ? { detail: workspace.marks.join(' · ') }
      : {}),
    icon: LayoutGridIcon,
    to: '/workspaces/$workspaceId',
    params: { workspaceId: workspace.id },
  }));
}

/**
 * The things inside the current workspace.
 *
 * Only the current one: its aggregate is the only one held, and fetching another's would
 * break FR-024. That limit is not hidden — `contentsSearchable` below is what the palette
 * uses to say the contents were *not searched* rather than reporting no matches.
 */
export function objectEntries(sources: EntrySources): PaletteEntry[] {
  const current = sources.current;
  if (!current) return [];

  /*
    One entry per workflow (spec 094 FR-016).

    There were two: a workflow and, separately, its board — which share a name by
    construction, so `detail` carried the difference to stop them reading as two
    identical rows. That was the right fix for the wrong problem. A board is what a
    published workflow looks like once work is moving through it, not a second
    object with the same name, and answering one search twice is the same
    duplication the navigation used to have.

    The descriptor still distinguishes a *draft* from a workflow with a board,
    because that difference is real and changes where the entry leads.
  */
  const workflows: PaletteEntry[] = current.workflows.map((workflow) => ({
    id: `workflow:${workflow.id}`,
    kind: 'object',
    group: 'in-workspace',
    label: workflow.name,
    detail: opensOnBoard(workflow) ? 'Board' : 'Workflow · draft',
    icon: opensOnBoard(workflow) ? SquareKanbanIcon : WorkflowIcon,
    // Asked rather than decided, so the palette agrees with the navigation row,
    // the list row and the `/workflows/{key}` redirect about where one workflow
    // opens (FR-009).
    ...workflowDestination(workflow, current.id),
  }));

  /*
    A task opens as itself: its board, with the drawer already on it. Every
    task entry used to point at the workspace task list with nothing selected,
    so searching a task by name landed the reader on an unfiltered list and
    left them to find it again — the palette had answered "where" with "in
    there somewhere". A task whose workflow is a draft, or whose workflow is
    not in the aggregate, has no board to open on and falls back to the list,
    which is at least the surface that can show it.
  */
  const workflowsById = new Map(current.workflows.map((w) => [w.id, w]));
  const tasks: PaletteEntry[] = current.tasks.map((task) => {
    const workflow = workflowsById.get(task.workflow_id);
    const destination =
      workflow && opensOnBoard(workflow)
        ? {
            ...workflowDestination(workflow, current.id),
            search: { task: task.id },
          }
        : {
            to: '/workspaces/$workspaceId/tasks',
            params: { workspaceId: current.id },
          };
    return {
      id: `task:${task.id}`,
      kind: 'object',
      group: 'in-workspace',
      label: task.title,
      detail: 'Task',
      // A task's own icon, not the Files folder (spec 129 FR-011).
      icon: ListTodoIcon,
      ...destination,
    };
  });

  return [...workflows, ...tasks];
}

/**
 * The actions.
 *
 * An action that cannot apply is **absent**, not offered and then refused (FR-016) — the
 * same rule as a gated page, for the same reason. The bar for adding one (FR-014): it
 * earns a row only if reaching it otherwise takes more than one click. A palette that
 * mirrors every button is a second menu, which is worse than no palette.
 */
export function actionEntries(sources: EntrySources): PaletteEntry[] {
  const entries: PaletteEntry[] = [
    {
      id: 'action:new-workspace',
      kind: 'action',
      group: 'actions',
      label: 'New workspace',
      icon: PlusIcon,
      act: 'new-workspace',
    },
    {
      id: 'action:toggle-theme',
      kind: 'action',
      group: 'actions',
      label: 'Switch theme',
      icon: SunMoonIcon,
      act: 'toggle-theme',
    },
  ];

  // On a board, not merely in a workspace. The action used to be offered
  // anywhere in a workspace and sent the reader to the task list with a
  // `create` parameter that page never declared, so the router dropped it and
  // nothing opened. A task is created on a board, so the action is offered
  // where that control is and runs it there.
  if (sources.current?.boardWorkflowKey && mayWork(sources)) {
    entries.unshift({
      id: 'action:new-task',
      kind: 'action',
      group: 'actions',
      label: 'New task',
      icon: PlusIcon,
      act: 'new-task',
    });
  }

  // The workspace's create verbs, each opening its form (spec 129 FR-009).
  if (sources.current && mayWork(sources)) {
    const creates: [ActionId, string, readonly string[]][] = [
      ['add-secret', 'Add secret', ['key', 'token', 'variable', 'env']],
      [
        'add-connection',
        'Add connection',
        ['github', 'gitlab', 'jira', 'integration'],
      ],
      ['add-execution-image', 'Add execution image', ['docker', 'container']],
      ['new-trigger', 'New trigger', ['automation', 'webhook']],
      ['add-workflow', 'Add workflow', ['library', 'install', 'template']],
      ['upload-file', 'Upload file', ['files', 'document']],
    ];
    for (const [act, label, keywords] of creates) {
      entries.push({
        id: `action:${act}`,
        kind: 'action',
        group: 'actions',
        label,
        icon: PlusIcon,
        act,
        keywords,
      });
    }
  }
  entries.push({
    id: 'action:shortcuts',
    kind: 'action',
    group: 'actions',
    label: 'Keyboard shortcuts',
    icon: KeyboardIcon,
    act: 'shortcuts',
    keywords: ['keys', 'hotkeys', 'help'],
  });

  return entries;
}

/** Everything offerable, in group order. */
export function buildEntries(sources: EntrySources): PaletteEntry[] {
  return [
    ...taskEntries(sources),
    ...actionEntries(sources),
    ...workspaceEntries(sources),
    ...objectEntries(sources),
    ...pageEntries(sources),
    ...personalEntries(sources.mcpOn),
  ];
}

/**
 * Whether the palette is in a position to search workspace contents at all.
 *
 * FR-012 exists because reporting "no results" when nothing was searched is a lie the
 * reader cannot detect — the same class of failure `OD-19` records on the usage surfaces.
 * The answer is structural rather than heuristic: contents are searchable exactly when a
 * workspace is current and its aggregate has resolved.
 */
export function contentsSearchable(sources: EntrySources): boolean {
  return sources.current !== undefined;
}

/**
 * Plain substring matching on names, case-insensitively.
 *
 * Names only — never descriptions, comments or transcripts, which the spec puts out of
 * scope. Nothing cleverer is assumed: fuzzy matching is a refinement, and a palette that
 * matches `usg` to "Usage" while nobody can predict what else it matches is worse than
 * one that does not.
 */
export function matches(entry: PaletteEntry, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return (
    entry.label.toLowerCase().includes(needle) ||
    (entry.keywords ?? []).some((word) => word.includes(needle))
  );
}
